/**
 * Inert spacer rows/cells for the windowed surfaces. A spacer absorbs the
 * off-screen extent so the scroll height + sticky headers stay correct while
 * only the visible slice is mounted. Spacers carry NONE of the interactive
 * data-attrs (cross/cycle/diagonal/feedback), are `aria-hidden` +
 * `role="presentation"` (AT never reads them as data cells), and take no
 * pointer events (a giant off-screen spacer must not swallow hover/clicks).
 */
import type { ReactNode } from "react";

/** A full-width vertical spacer row (top/bottom of a windowed `tbody`). */
export function SpacerRow({ height, cols }: { readonly height: number; readonly cols: number }): ReactNode {
  if (height <= 0) return null;
  return (
    <tr className="arch-spacer" aria-hidden="true" role="presentation" style={{ height }}>
      <td colSpan={Math.max(1, cols)} style={{ padding: 0, border: 0 }} />
    </tr>
  );
}

/** A horizontal spacer cell (left/right of a windowed column slice). `as="th"`
 *  for the header row so it stays a header cell; `td` for body rows. */
export function SpacerCell({
  width,
  as = "td",
}: {
  readonly width: number;
  readonly as?: "td" | "th";
}): ReactNode {
  if (width <= 0) return null;
  const style = { width, minWidth: width, maxWidth: width, padding: 0, border: 0 } as const;
  return as === "th" ? (
    <th className="arch-spacer" aria-hidden="true" style={style} />
  ) : (
    <td className="arch-spacer" aria-hidden="true" style={style} />
  );
}
